import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '../components/Icon';
import DateTimeField, { bugunISO } from '../components/DateTimeField';
import { useNavigate, useLocation } from 'react-router-dom';
import API from '../services/api';
import { fmtShortTime } from '../utils/date';
import { useLanguage } from '../context/LanguageContext';
import OtaqSecici from '../components/OtaqSecici';
import './Requests.css';

const IT_CATEGORIES = ['Avadanlıq','Proqram təminatı','Şəbəkə','Giriş / İcazə','E-poçt','Printer','Telefon','Digər'];
const IT_OS = ['Windows 10','Windows 11','macOS','Linux','Android','iOS','Digər'];

const STATUS = {
  open:        { label: 'Açıq',         color: '#6b7280', bg: '#f3f4f6' },
  assigned:    { label: 'Təyin edildi',  color: '#7c3aed', bg: '#ede9fe' },
  in_progress: { label: 'İcrada',       color: '#0369a1', bg: '#e0f2fe' },
  pending:     { label: 'Gözləmə',      color: '#d97706', bg: '#fef3c7' },
  resolved:    { label: 'Həll edildi',   color: '#059669', bg: '#d1fae5' },
  closed:      { label: 'Bağlandı',      color: '#374151', bg: '#e5e7eb' },
  cancelled:   { label: 'Ləğv edildi',   color: '#dc2626', bg: '#fee2e2' },
};

const STATUS_AZ = {
  open:        'Açıq',
  assigned:    'Təyin edildi',
  in_progress: 'İcrada',
  pending:     'Gözləmədə',
  resolved:    'Həll edildi',
  closed:      'Bağlandı',
  cancelled:   'Ləğv edildi',
};

const PRIORITY = {
  urgent: { label: 'Təcili', color: '#dc2626', sla: '2 saat' },
  high:   { label: 'Yüksək', color: '#d97706', sla: '8 saat' },
  medium: { label: 'Orta',   color: '#2563eb', sla: '24 saat' },
  low:    { label: 'Aşağı',  color: '#6b7280', sla: '72 saat' },
};

function fmtTime(mins) {
  if (!mins && mins !== 0) return '-';
  const h = Math.floor(mins / 60), m = mins % 60;
  return h > 0 ? (h + 's ' + m + 'd') : (m + 'd');
}

function LiveTimer({ acceptedAt, resolvedAt }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (resolvedAt) {
      setElapsed(Math.round((new Date(resolvedAt) - new Date(acceptedAt)) / 1000));
      return;
    }
    const tick = () => setElapsed(Math.round((Date.now() - new Date(acceptedAt)) / 1000));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [acceptedAt, resolvedAt]);
  const s = elapsed % 60, m = Math.floor(elapsed / 60) % 60, h = Math.floor(elapsed / 3600);
  const pad = n => String(n).padStart(2,'0');
  const display = h > 0 ? (h + 's ' + pad(m) + 'd ' + pad(s) + 'san') : (pad(m) + 'd ' + pad(s) + 'san');
  return React.createElement('span', { className: 'live-timer' }, display);
}

function Stars({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return React.createElement('div', { className: 'stars' },
    [1,2,3,4,5].map(n =>
      React.createElement('button', {
        key: n, type: 'button',
        className: 'star' + (n <= (hover || value) ? ' filled' : ''),
        onMouseEnter: () => onChange && setHover(n),
        onMouseLeave: () => onChange && setHover(0),
        onClick: () => onChange && onChange(n)
      }, React.createElement(Icon, { name: 'star', size: 20, filled: true }))
    )
  );
}

export default function Requests() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';

  const [tickets, setTickets] = useState([]);
  const [departments, setDepartments] = useState([]);

  /* Sorğu qəbul etməyən şöbələr siyahıda göstərilmir */
  const visibleDepts = React.useMemo(() => departments.filter(d => {
    const code = (d.code || '').toUpperCase();
    const name = (d.name || '').toLowerCase();
    if (['WAREHOUSE', 'STOCK', 'GENERAL'].includes(code)) return false;
    if (name.includes('anbar') || name.includes('ümumi kompleks')
        || name.includes('umumi kompleks') || name === 'general complex') return false;
    return true;
  }), [departments]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [activeDept, setActiveDept] = useState(null); // selected department view

  /* Şöbələrin görünüşü: sıra (geniş kartlar) yoxsa kafel (kvadrat
     xanalar). Seçim brauzerdə qalır — yan paneldəki kimi. */
  const [deptGorunus, setDeptGorunus] = useState(() => {
    try {
      return localStorage.getItem('ems_sobe_gorunus') === 'kafel' ? 'kafel' : 'sira';
    } catch { return 'sira'; }
  });
  const deptGorunusSec = (ad) => {
    setDeptGorunus(ad);
    try { localStorage.setItem('ems_sobe_gorunus', ad); } catch {}
  };
  /* Admin bütün kampusları görəndə əvvəlcə kampus seçir, sonra şöbələr açılır.
     Əks halda eyni adlı 22 kart yan-yana düşür və hansının hansı olduğu itir. */
  const [activeBranch, setActiveBranch] = useState(null);
  const [detail, setDetail] = useState(null);
  const [comments, setComments] = useState([]);
  const [audit, setAudit] = useState([]);
  const [detailTab, setDetailTab] = useState('info');
  const [itDeptId, setItDeptId] = useState(null);
  const [hrDeptId, setHrDeptId] = useState(null);
  const [financeDeptId, setFinanceDeptId] = useState(null);

  const [fDept, setFDept] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fPriority, setFPriority] = useState('');
  const [fMine, setFMine] = useState(false);
  const [fAssignMe, setFAssignMe] = useState(false);

  const [showNew, setShowNew] = useState(false);
  const [newDept, setNewDept] = useState('');
  const [newType, setNewType] = useState('');
  const [reqTypes, setReqTypes] = useState([]);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newPriority, setNewPriority] = useState('medium');
  const [newCategory, setNewCategory] = useState('');
  const [newDevice, setNewDevice] = useState('');
  const [newOS, setNewOS] = useState('');
  const [newErrorMsg, setNewErrorMsg] = useState('');
  const [newAbsType, setNewAbsType] = useState('');
  const [newFamReason, setNewFamReason] = useState('');
  const [newEduDoc, setNewEduDoc] = useState('');
  const [newEduFiles, setNewEduFiles] = useState([]);
  const [newRoom, setNewRoom] = useState('');
  const [newLeaveStart, setNewLeaveStart] = useState('');
  const [newLeaveEnd, setNewLeaveEnd] = useState('');
  const [newLeaveExpl, setNewLeaveExpl] = useState('');
  const [newInstallMonths, setNewInstallMonths] = useState('');
  const [newPaySlipMonth, setNewPaySlipMonth] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [deptMembers, setDeptMembers] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [assignTo, setAssignTo] = useState('');
  const [assignNote, setAssignNote] = useState('');

  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  /* Şərhə qoşulan fayllar */
  const [attachments, setAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadingAtt, setUploadingAtt] = useState(false);

  const [showRate, setShowRate] = useState(false);
  const [rateVal, setRateVal] = useState(0);
  const [rateComment, setRateComment] = useState('');

  const [showResolve, setShowResolve] = useState(false);
  const [resolution, setResolution] = useState('');

  // Forma 1 təsdiq zənciri
  const [legalFlow, setLegalFlow] = useState(null);
  const [legalBusy, setLegalBusy] = useState(false);

  const LEGAL_STEPS = [
    { k: 'lawyer_review', l: t('legal.stepLawyer'),   who: 'Hüquqşünas' },
    { k: 'finance',       l: t('legal.stepFinance'),  who: 'Mehman Babayev' },
    { k: 'director',      l: t('legal.stepDirector'), who: 'Tural Abbasov' },
    { k: 'execution',     l: t('legal.stepExecution'),who: 'Hüquqşünas' },
  ];

  const loadLegalFlow = useCallback(async (id) => {
    try {
      const r = await API.get('/legal/' + id + '/flow');
      setLegalFlow(r.data);
    } catch { setLegalFlow(null); }
  }, []);

  const legalAct = async (action) => {
    if (!detail) return;
    if (action === 'reject') {
      const note = window.prompt(t('legal.rejectReason'));
      if (note === null) return;
      setLegalBusy(true);
      try {
        await API.put('/legal/' + detail.id + '/reject', { note });
        await Promise.all([loadLegalFlow(detail.id), load()]);
      } catch (e) { alert(e.response?.data?.message || t('legal.actionFailed')); }
      finally { setLegalBusy(false); }
      return;
    }
    setLegalBusy(true);
    try {
      await API.put('/legal/' + detail.id + '/approve', {});
      await Promise.all([loadLegalFlow(detail.id), load()]);
    } catch (e) { alert(e.response?.data?.message || t('legal.actionFailed')); }
    finally { setLegalBusy(false); }
  };

  const [loadError, setLoadError] = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const p = {};
      if (fDept) p.department_id = fDept;
      if (fStatus) p.status = fStatus;
      if (fPriority) p.priority = fPriority;
      if (fMine) p.mine = '1';
      if (fAssignMe) p.assignee_me = '1';
      const res = await API.get('/requests', { params: p });
      setTickets(res.data);
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.error || e.message || 'Network error';
      setLoadError(`${e.response?.status || ''} ${msg}`.trim());
      console.error('requests load failed:', e.response?.status, msg, e.response?.data);
    } finally { setLoading(false); }
  }, [fDept, fStatus, fPriority, fMine, fAssignMe]);

  useEffect(() => { load(); }, [load]);

  // Başqa səhifədən konkret sorğu ilə gəliblərsə, onu birbaşa açırıq.
  // (Əvvəl bu işləmirdi: useLocation idxal olunmadığı üçün `location`
  //  qlobal window.location idi və onun state sahəsi yoxdur.)
  // Gələn göstərişi BİR DƏFƏ tuturuq: aşağıda router state-i təmizlənir,
  // şöbə isə departments yüklənəndən sonra lazım olur.
  const [incoming, setIncoming] = useState(() => {
    if (location.state?.ticketId)
      return { ticketId: location.state.ticketId, deptId: location.state.deptId };
    // Bildirişdən gələn "/requests?ticket=26" linki də sorğunu açır
    const qid = new URLSearchParams(location.search).get('ticket');
    return qid ? { ticketId: parseInt(qid, 10) } : null;
  });

  useEffect(() => {
    if (!incoming?.ticketId) return;
    openDetail({ id: incoming.ticketId });
    navigate(location.pathname, { replace: true, state: {} });   // geri qayıdanda təkrar açılmasın
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incoming]);

  // Sorğunun öz şöbəsini fonda açırıq ki, modalı bağlayanda siyahıda qalsın
  useEffect(() => {
    if (!incoming?.deptId || !departments.length || activeDept) return;
    const d = departments.find(x => String(x.id) === String(incoming.deptId));
    if (d) { setActiveDept(d); setFDept(String(d.id)); setIncoming(p => ({ ...p, deptId: null })); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departments, incoming]);


  useEffect(() => {
    API.get('/departments').then(r => {
      setDepartments(r.data);
      const it = r.data.find(d => d.code === 'IT' || (d.name || '').toUpperCase().includes('IT'));
      if (it) setItDeptId(it.id);
      const hr = r.data.find(d => d.code === 'HR' || (d.name || '').toUpperCase().includes('HR') || (d.name || '').includes('İnsan'));
      if (hr) setHrDeptId(hr.id);
      const fin = r.data.find(d => d.code === 'FINANCE' || (d.name || '').includes('Maliyyə'));
      if (fin) setFinanceDeptId(fin.id);
    });
  }, []);

  // Real-time ticket updates via socket (bridged through ChatWidget)
  useEffect(() => {
    const SLA_HOURS = { urgent: 2, high: 8, medium: 24, low: 72 };
    function addSla(t) {
      const sla = SLA_HOURS[t.priority] || 24;
      const deadline = new Date(new Date(t.created_at).getTime() + sla * 3600000);
      return { ...t, sla_deadline: deadline, sla_breached: !['resolved','closed','cancelled'].includes(t.status) && new Date() > deadline, sla_hours: sla };
    }
    const onNew = (e) => {
      const ticket = addSla(e.detail);
      setTickets(prev => {
        if (prev.find(t => t.id === ticket.id)) return prev;
        return [ticket, ...prev];
      });
    };
    const onUpdated = (e) => {
      const ticket = addSla(e.detail);
      setTickets(prev => prev.map(t => t.id === ticket.id ? ticket : t));
      // Also update open detail if same ticket
      setDetail(prev => prev && prev.id === ticket.id ? { ...prev, ...ticket } : prev);
    };
    window.addEventListener('ems:ticket:new', onNew);
    window.addEventListener('ems:ticket:updated', onUpdated);
    return () => {
      window.removeEventListener('ems:ticket:new', onNew);
      window.removeEventListener('ems:ticket:updated', onUpdated);
    };
  }, []);

  useEffect(() => {
    if (!newDept) { setReqTypes([]); setNewType(''); return; }
    API.get('/departments/' + newDept + '/types').then(r => { setReqTypes(r.data); setNewType(''); });
  }, [newDept]);

  const openDetail = async (t) => {
    setSelected(t);
    setDetailTab('info');
    setShowRate(false); setShowAssign(false); setShowResolve(false);
    try {
      const [d, c, a, att] = await Promise.all([
        API.get('/requests/' + t.id),
        API.get('/requests/' + t.id + '/comments'),
        API.get('/requests/' + t.id + '/audit'),
        API.get('/requests/' + t.id + '/attachments').catch(() => ({ data: [] })),
      ]);
      setDetail(d.data);
      setComments(c.data);
      setAudit(a.data);
      setAttachments(att.data || []);
      setPendingFiles([]);
      // Forma 1 sorğusudursa təsdiq zəncirini də yüklə
      const cfd = typeof d.data.custom_fields === 'string'
        ? (() => { try { return JSON.parse(d.data.custom_fields); } catch { return {}; } })()
        : (d.data.custom_fields || {});
      if (cfd.form === 'legal_contract_f1') loadLegalFlow(d.data.id); else setLegalFlow(null);
      if (d.data.department_id) {
        API.get('/requests/dept/' + d.data.department_id + '/members').then(r => setDeptMembers(r.data)).catch(() => {});
      }
    } catch (e) {}
  };

  const closeDetail = () => { setSelected(null); setDetail(null); };
  const isAssignee = (t) => t && t.assignee_id === user.id;
  const isRequester = (t) => t && t.requester_id === user.id;

  /* Həll edilib, amma sorğu sahibi hələ qiymətləndirməyib.
     Funksiya əvvəl də vardı, lakin yalnız sorğunu açanda görünürdü —
     7 həll edilmiş sorğudan heç biri qiymətləndirilməmişdi. */
  const awaitingRating = (tickets || []).filter(
    t => t.status === 'resolved' && !t.rating && isRequester(t));
  const isIT = (t) => t && ((t.department_code === 'IT') || ((t.department_name || '').toUpperCase().includes('IT')));
  const isHR = (t) => t && ((t.department_code === 'HR') || ((t.department_name || '').toUpperCase().includes('HR')) || ((t.department_name || '').includes('İnsan')));
  const isNewIT = itDeptId && parseInt(newDept) === parseInt(itDeptId);
  const isNewHR = hrDeptId && parseInt(newDept) === parseInt(hrDeptId);
  const isNewFinance = financeDeptId && parseInt(newDept) === parseInt(financeDeptId);

  const submitNew = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isNewHR) {
        // HR: submit as leave request
        if (!newAbsType || !newLeaveStart || !newLeaveEnd) {
          alert('Növ, başlama və bitmə tarixini doldurun'); setSubmitting(false); return;
        }
        // Keçmiş günə ərizə yoxdur; bugünkü gün üçün saat sərbəstdir
        if (newLeaveStart.slice(0, 10) < bugunISO()) { alert(t('leave.pastDateAlert')); setSubmitting(false); return; }
        if (newLeaveEnd < newLeaveStart) { alert(t('leave.endBeforeStartAlert')); setSubmitting(false); return; }
        if (newAbsType === 'education' && newEduDoc === 'yes' && !newEduFiles.length) {
          alert('Sənəd var seçmisiniz — zəhmət olmasa sənədin özünü də əlavə edin.'); setSubmitting(false); return;
        }
        const lv = await API.post('/leave', {
          absence_type: newAbsType,
          family_reason: newAbsType === 'family' ? newFamReason : undefined,
          education_has_doc: newAbsType === 'education' ? (newEduDoc === 'yes' ? 1 : 0) : undefined,
          explanation: newLeaveExpl,
          start_date: newLeaveStart,
          end_date: newLeaveEnd,
        });
        if (newEduFiles.length && lv.data?.id) {
          const fd = new FormData();
          newEduFiles.forEach(f => fd.append('files', f));
          await API.post('/leave/' + lv.data.id + '/attachments', fd,
            { headers: { 'Content-Type': 'multipart/form-data' } });
        }
        setShowNew(false);
        setNewAbsType(''); setNewFamReason(''); setNewEduDoc(''); setNewEduFiles([]); setNewLeaveStart(''); setNewLeaveEnd(''); setNewLeaveExpl('');
      } else {
        if (!newDept || !newTitle.trim()) { setSubmitting(false); return; }
        const custom = {};
        if (newCategory) custom.category = newCategory;
        if (newRoom) custom.room = newRoom;
        if (newDevice) custom.device = newDevice;
        if (newOS) custom.os = newOS;
        if (newErrorMsg) custom.error_msg = newErrorMsg;
        if (newInstallMonths) custom.installment_months = newInstallMonths;
        if (newPaySlipMonth) custom.pay_slip_month = newPaySlipMonth;
        await API.post('/requests', {
          department_id: newDept,
          request_type_id: newType || null,
          title: newTitle.trim(),
          description: newDesc.trim(),
          priority: newPriority,
          custom_fields: Object.keys(custom).length ? custom : null,
        });
        setShowNew(false);
        setNewDept(''); setNewType(''); setNewTitle(''); setNewDesc('');
        setNewPriority('medium'); setNewCategory(''); setNewRoom(''); setNewDevice(''); setNewOS(''); setNewErrorMsg('');
        setNewInstallMonths(''); setNewPaySlipMonth('');
        load();
      }
    } catch (e) { alert(e.response?.data?.error || 'Xəta baş verdi'); } finally { setSubmitting(false); }
  };

  const doAssign = async () => {
    if (!assignTo) return;
    try {
      await API.put('/requests/' + detail.id + '/assign', { assignee_id: parseInt(assignTo), note: assignNote });
      setShowAssign(false); setAssignTo(''); setAssignNote('');
      openDetail(detail); load();
    } catch (e) { alert(e.response?.data?.error || 'Xəta'); }
  };

  const doStart = async () => {
    try {
      await API.put('/requests/' + detail.id + '/start');
      openDetail(detail); load();
    } catch (e) { alert(e.response?.data?.error || 'Xəta'); }
  };

  const doResolve = async () => {
    try {
      await API.put('/requests/' + detail.id + '/resolve', { resolution });
      setShowResolve(false); setResolution('');
      openDetail(detail); load();
    } catch (e) { alert(e.response?.data?.error || 'Xəta'); }
  };

  const doRate = async () => {
    if (rateVal < 1) return alert('Zəhmət olmasa qiymət seçin');
    try {
      await API.put('/requests/' + detail.id + '/rate', { rating: rateVal, comment: rateComment });
      setShowRate(false); setRateVal(0); setRateComment('');
      openDetail(detail); load();
    } catch (e) { alert(e.response?.data?.error || 'Xəta'); }
  };

  const addComment = async (e) => {
    e.preventDefault();
    // Yalnız fayl göndərmək də olar — mətn məcburi deyil
    if (!newComment.trim() && !pendingFiles.length) return;
    setUploadingAtt(true);
    try {
      const text = newComment.trim() || (pendingFiles.length === 1 ? 'Fayl əlavə edildi' : 'Fayllar əlavə edildi');
      const r = await API.post('/requests/' + detail.id + '/comments',
        { comment: text, is_internal: isInternal });

      if (pendingFiles.length) {
        const fd = new FormData();
        pendingFiles.forEach(f => fd.append('files', f));
        fd.append('comment_id', String(r.data.id));
        await API.post('/requests/' + detail.id + '/attachments', fd,
          { headers: { 'Content-Type': 'multipart/form-data' } });
      }

      setNewComment('');
      setPendingFiles([]);
      const [c, att] = await Promise.all([
        API.get('/requests/' + detail.id + '/comments'),
        API.get('/requests/' + detail.id + '/attachments').catch(() => ({ data: [] })),
      ]);
      setComments(c.data);
      setAttachments(att.data || []);
    } catch (err) {
      alert(err.response?.data?.message || t('common.error'));
    } finally { setUploadingAtt(false); }
  };

  /* Fayl auth tələb edir — blob kimi yüklənib yeni pəncərədə açılır */
  const openAttachment = async (a) => {
    try {
      const r = await API.get('/requests/' + detail.id + '/attachments/' + a.id, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      alert(err.response?.data?.message || t('common.error'));
    }
  };

  const fmtSize = (b) => !b ? '' :
    b < 1024 ? b + ' B' :
    b < 1048576 ? Math.round(b / 1024) + ' KB' :
    (b / 1048576).toFixed(1) + ' MB';

  const fmt = (d) => d ? new Date(d).toLocaleString('az-AZ') : '-';
  const fmtShort = (d) => fmtShortTime(d, lang) || '-';

  const getCF = (d) => {
    if (!d || !d.custom_fields) return {};
    try { return typeof d.custom_fields === 'string' ? JSON.parse(d.custom_fields) : d.custom_fields; }
    catch (e) { return {}; }
  };


  const DEPT_ICONS = {
    IT:        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>,
    HR:        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="7" r="3"/><path d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2"/><path d="M16 3.13a4 4 0 010 7.75M21 21v-2a4 4 0 00-3-3.87"/></svg>,
    FINANCE:   <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>,
    SUPPLY:    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.7 13.4a2 2 0 002 1.6h9.7a2 2 0 002-1.6L23 6H6"/></svg>,
    ADMISSION: <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5a6 3 0 0012 0v-5"/></svg>,
    ACADEMIC:  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></svg>,
    HIK:       <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 000-7.78z"/></svg>,
    MARKETING: <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l18-8v18l-18-8z"/><path d="M11.6 16.8a3 3 0 11-5.8-1.6"/></svg>,
    MAINTENANCE: <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94L14.7 6.3z"/></svg>,
    LEGAL:     <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v18M7 21h10"/><path d="M5 7h14"/><path d="M5 7l-3 6a3 3 0 006 0zM19 7l-3 6a3 3 0 006 0z"/></svg>,
    GENERAL:   <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/></svg>,
  };

  const DEPT_THEME = {
    IT:          { color:'#0d9488', soft:'#ccfbf1', tint:'#f0fdfa' },
    HR:          { color:'#7c3aed', soft:'#ede9fe', tint:'#f5f3ff' },
    FINANCE:     { color:'#d97706', soft:'#fef3c7', tint:'#fffbeb' },
    SUPPLY:      { color:'#059669', soft:'#d1fae5', tint:'#ecfdf5' },
    ADMISSION:   { color:'#2563eb', soft:'#dbeafe', tint:'#eff6ff' },
    ACADEMIC:    { color:'#dc2626', soft:'#fee2e2', tint:'#fef2f2' },
    HIK:         { color:'#ca8a04', soft:'#fef9c3', tint:'#fefce8' },
    MARKETING:   { color:'#db2777', soft:'#fce7f3', tint:'#fdf2f8' },
    MAINTENANCE: { color:'#475569', soft:'#e2e8f0', tint:'#f1f5f9' },
    LEGAL:       { color:'#4f46e5', soft:'#e0e7ff', tint:'#eef2ff' },
    GENERAL:     { color:'#6366f1', soft:'#e0e7ff', tint:'#eef2ff' },
  };

  const DEPT_DESCS = {
    IT:          t('requests.deptIT'),
    HR:          t('requests.deptHR'),
    FINANCE:     t('requests.deptFINANCE'),
    SUPPLY:      t('requests.deptSUPPLY'),
    ADMISSION:   t('requests.deptADMISSION'),
    ACADEMIC:    t('requests.deptACADEMIC'),
    HIK:         t('requests.deptHIK'),
    MARKETING:   t('requests.deptMARKETING'),
    MAINTENANCE: t('requests.deptMAINTENANCE'),
    LEGAL:       t('requests.deptLEGAL'),
    GENERAL:     t('requests.deptGENERAL'),
  };

  const STATUS_LABELS = {
    open:        { label: t('requests.statusOpen'),       color: '#6b7280', bg: '#f3f4f6' },
    assigned:    { label: t('requests.statusAssigned'),    color: '#7c3aed', bg: '#ede9fe' },
    in_progress: { label: t('requests.statusInProgress'),  color: '#0369a1', bg: '#e0f2fe' },
    pending:     { label: t('requests.statusPending'),     color: '#d97706', bg: '#fef3c7' },
    resolved:    { label: t('requests.statusResolved'),    color: '#059669', bg: '#d1fae5' },
    closed:      { label: t('requests.statusClosed'),      color: '#374151', bg: '#e5e7eb' },
    cancelled:   { label: t('requests.statusCancelled'),   color: '#dc2626', bg: '#fee2e2' },
  };
  const PRIORITY_LABELS = {
    urgent: { label: t('requests.priorityUrgent'), color: '#dc2626' },
    high:   { label: t('requests.priorityHigh'),   color: '#d97706' },
    medium: { label: t('requests.priorityMedium'), color: '#2563eb' },
    low:    { label: t('requests.priorityLow'),    color: '#6b7280' },
  };

  /* İnsan Resursları və Təchizat kartları 'requests' cədvəlindən
     sayılırdı, lakin onların işi ayrı cədvəllərdədir — işburaxma
     ərizələri və tələbnamələr. Ona görə kartlarda həmişə "—" görünürdü,
     halbuki içəridə gözləyən sənədlər vardı. */
  /* Saylar KAMPUS üzrə saxlanılır. Əvvəl tək rəqəm idi və kartlara şöbə
     KODU ilə verilirdi — hər iki kampusun HR şöbəsinin kodu 'HR' olduğu
     üçün admin "bütün kampuslar" rejimində eyni rəqəmi iki dəfə görürdü. */
  const [sideCounts, setSideCounts] = useState({ HR: {}, SUPPLY: {} });

  useEffect(() => {
    let dead = false;
    (async () => {
      const [lv, pr] = await Promise.all([
        API.get('/leave').catch(() => ({ data: [] })),
        API.get('/procurement', { params: { active: 1 } }).catch(() => ({ data: [] })),
      ]);
      if (dead) return;
      const qrupla = (list) => list.reduce((acc, x) => {
        const k = String(x.branch_id ?? 'yox');
        acc[k] = (acc[k] || 0) + 1;
        return acc;
      }, {});
      const pendingLeave = (lv.data || []).filter(
        x => ['submitted', 'manager_approved', 'hr_approved'].includes(x.status));
      setSideCounts({ HR: qrupla(pendingLeave), SUPPLY: qrupla(pr.data || []) });
    })();
    return () => { dead = true; };
  }, []);

  /* Şöbələr kampuslara görə qruplaşır. Baş Kampus həmişə birinci. */
  const kampuslar = React.useMemo(() => {
    const m = new Map();
    visibleDepts.forEach(d => {
      const kod = d.branch_code || 'MAIN';
      if (!m.has(kod)) m.set(kod, { code: kod, name: d.branch_name || kod, depts: [] });
      m.get(kod).depts.push(d);
    });
    return [...m.values()].sort((a, b) =>
      a.code === 'MAIN' ? -1 : b.code === 'MAIN' ? 1 : a.name.localeCompare(b.name));
  }, [visibleDepts]);

  // Yalnız admin birdən çox kampus görəndə seçim mərhələsi çıxır
  const kampusSecimi = isAdmin && kampuslar.length > 1 && !activeBranch;
  const gosterilenDepts = activeBranch
    ? visibleDepts.filter(d => (d.branch_code || 'MAIN') === activeBranch)
    : visibleDepts;
  const aktivKampus = kampuslar.find(k => k.code === activeBranch);

  /* "AKTİV" sözü işi bitməmiş sorğu deməkdir. Əvvəl bütün sorğular
     sayılırdı: IT-də 15 yazılırdı, halbuki 12-si həll olunub bağlanmışdı,
     aktiv olan cəmi 3 idi. HR və Təchizat sayları onsuz da süzülürdü —
     yalnız şöbə ticketləri süzülmürdü. */
  const BITMIS = ['resolved', 'closed', 'cancelled'];
  const deptTicketCount = (deptId) => tickets.filter(t =>
    String(t.department_id) === String(deptId) && !BITMIS.includes(t.status)).length;

  /* Kartda göstərilən say: HR və Təchizat öz cədvəllərindən */
  const deptActiveCount = (d) => {
    const code = (d.code || '').toUpperCase();
    const br = String(d.branch_id ?? 'yox');
    if (code === 'HR') return sideCounts.HR[br] || 0;
    if (code === 'SUPPLY') return sideCounts.SUPPLY[br] || 0;
    return deptTicketCount(d.id);
  };
  // "New" = open status, not yet assigned/in_progress
  const deptNewCount = (deptId) => tickets.filter(t =>
    String(t.department_id) === String(deptId) &&
    (t.status === 'open' || t.status === 'pending' || t.status === 'submitted')
  ).length;

  const selectDept = (dept) => {
    /* Kampus seçilibsə, HR və Təchizat səhifələri də həmin kampusla
       məhdudlaşır — əks halda Xankəndinin HR kartına girəndə Baş Kampusun
       ərizələri açılardı. */
    const brQ = activeBranch && dept.branch_id ? '?branch=' + dept.branch_id : '';
    if (dept.code === 'HR') { navigate('/leave' + brQ); return; }
    if (dept.code === 'SUPPLY') { navigate('/procurement' + brQ); return; }
    if (dept.code === 'ADMISSION') { navigate('/requests/new', { state: { deptCode: 'ADMISSION' } }); return; }
    // Other departments: show their tickets filtered
    setActiveDept(dept);
    setFDept(String(dept.id));
  };

  const backToDepts = () => { setActiveDept(null); setFDept(''); };
  const backToBranches = () => { setActiveBranch(null); setActiveDept(null); setFDept(''); };

  // Admin-only: delete a single request (comments + audit go with it)
  const deleteTicket = async (tk, e) => {
    e.stopPropagation();
    if (!window.confirm(`${tk.request_number} — "${tk.title}" silinsin? Bu geri qaytarıla bilməz.`)) return;
    try {
      await API.delete('/requests/' + tk.id);
      setTickets(prev => prev.filter(x => x.id !== tk.id));
      setDetail(prev => prev && prev.id === tk.id ? null : prev);
    } catch (err) {
      alert(err.response?.data?.message || 'Silinmədi');
    }
  };

  // Admin-only: wipe every request in the open department
  const deleteAllInDept = async () => {
    if (!activeDept) return;
    const n = tickets.length;
    if (!n) return;
    if (!window.confirm(`${activeDept.name} şöbəsindəki ${n} sorğunun HAMISI silinsin? Bu geri qaytarıla bilməz.`)) return;
    if (!window.confirm('Əminsiniz? Sorğu nömrələri yenidən 001-dən başlayacaq.')) return;
    try {
      const r = await API.delete('/requests/bulk', { params: { department_id: activeDept.id } });
      setTickets([]);
      alert(`${r.data.deleted} sorğu silindi.`);
    } catch (err) {
      alert(err.response?.data?.message || 'Silinmədi');
    }
  };

  return (
    <div className="req-page">
      {/* Department cards */}
      {!activeDept && (
        <>
          <div className="req-header req-header-modern">
            {aktivKampus && (
              <button type="button" className="req-back-btn" onClick={backToBranches}
                      title={t('requests.backToCampuses')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
              </button>
            )}
            <div className="req-header-text">
              <h2>{aktivKampus ? aktivKampus.name : t('sidebar.requests')}</h2>
              <p className="req-subtitle">
                {kampusSecimi ? t('requests.campusPickSubtitle') : t('requests.pageSubtitle')}
              </p>
            </div>
            {/* Görünüş seçimi: sıra ↔ kafel */}
            <div className="rh-view" role="radiogroup" aria-label={t('requests.viewLabel')}>
              <button type="button" role="radio" aria-checked={deptGorunus === 'sira'}
                      className={'rh-view-btn' + (deptGorunus === 'sira' ? ' on' : '')}
                      onClick={() => deptGorunusSec('sira')} title={t('requests.viewRows')}>
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
                     strokeWidth="2.2" strokeLinecap="round">
                  <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
                  <line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/>
                  <line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                </svg>
              </button>
              <button type="button" role="radio" aria-checked={deptGorunus === 'kafel'}
                      className={'rh-view-btn' + (deptGorunus === 'kafel' ? ' on' : '')}
                      onClick={() => deptGorunusSec('kafel')} title={t('requests.viewTiles')}>
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
                     strokeWidth="2.2" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" rx="1.5"/>
                  <rect x="14" y="3" width="7" height="7" rx="1.5"/>
                  <rect x="3" y="14" width="7" height="7" rx="1.5"/>
                  <rect x="14" y="14" width="7" height="7" rx="1.5"/>
                </svg>
              </button>
            </div>
            {/* Üç göstərici: aktiv sorğu, şöbə, işçi */}
            <div className="rh-stats">
              <div className="rh-stat">
                {/* Başlıqdaki say kartlardakı sayların cəmidir —
                    HR və Təchizat öz cədvəllərindən gəlir */}
                <b>{gosterilenDepts.reduce((s, d) => s + deptActiveCount(d), 0)}</b>
                <span>{t('requests.activeRequestsBig')}</span>
              </div>
              <span className="rh-sep" />
              <div className="rh-stat">
                <b>{gosterilenDepts.length}</b>
                <span>{t('requests.deptSuffix')}</span>
              </div>
              <span className="rh-sep" />
              <div className="rh-stat">
                <b>{gosterilenDepts.reduce((s, d) => s + (d.member_count || 0), 0)}</b>
                <span>{t('requests.employeeSuffix')}</span>
              </div>
            </div>
          </div>

          {awaitingRating.length > 0 && (
            <div className="rate-bar">
              <span className="rate-bar-ico"><Icon name="star" size={18} filled /></span>
              <div className="rate-bar-txt">
                <b>{awaitingRating.length}</b> {t('requests.awaitingRating')}
                <div className="rate-bar-sub">{t('requests.awaitingRatingSub')}</div>
              </div>
              <div className="rate-bar-list">
                {awaitingRating.slice(0, 3).map(t2 => (
                  <button key={t2.id} type="button" className="rate-bar-btn"
                    onClick={() => openDetail(t2)}>
                    {t2.request_number}
                  </button>
                ))}
                {awaitingRating.length > 3 && (
                  <span className="rate-bar-more">+{awaitingRating.length - 3}</span>
                )}
              </div>
            </div>
          )}

          {/* Addım 1 — kampus seçimi (yalnız admin, birdən çox kampus olanda) */}
          {kampusSecimi ? (
            <div className={'dept-cards-grid kampus-grid' + (deptGorunus === 'kafel' ? ' kafel' : '')}>
              {kampuslar.map(k => {
                const aktiv = k.depts.reduce((s, d) => s + deptActiveCount(d), 0);
                const isci  = k.depts.reduce((s, d) => s + (d.member_count || 0), 0);
                const yeni  = k.depts.reduce((s, d) => s + deptNewCount(d.id), 0);
                return (
                  <div key={k.code}
                       className={'dept-req-card kampus-card' + (yeni > 0 ? ' has-new' : '')
                         + (k.code !== 'MAIN' ? ' other-branch' : '')}
                       onClick={() => setActiveBranch(k.code)}>
                    <span className="dept-req-accent" aria-hidden="true" />
                    {yeni > 0 && <span className="dept-new-badge">{yeni}</span>}
                    <div className="dept-req-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4"/><path d="M9 9v.01M9 12v.01M9 15v.01M9 18v.01"/>
                      </svg>
                    </div>
                    <div className="dept-req-info">
                      <div className="dept-req-name">{k.name}</div>
                      <div className="dept-req-desc">
                        {k.depts.length} {t('requests.deptSuffix')} · {isci} {t('requests.employeeSuffix')}
                      </div>
                    </div>
                    <div className={'dept-stat' + (aktiv > 0 ? ' on' : '')}>
                      <b>{aktiv > 0 ? aktiv : '—'}</b>
                      <span>{t('requests.activeSuffix')}</span>
                    </div>
                    <span className="dept-req-arrow">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
          <div className={'dept-lovhe-qab' + (deptGorunus === 'kafel' ? ' kafel-qab' : '')}>
          {/* Sütun başlığı — lövhəni siyahı yox, cədvəl kimi oxudur.
              "AKTİV" sözü hər sətirdə təkrarlanmır, bir dəfə burada yazılır. */}
          {deptGorunus !== 'kafel' && (
            <div className="lovhe-bas" aria-hidden="true">
              <span className="lb-no">№</span>
              <span className="lb-ad">{t('requests.colDept')}</span>
              <span className="lb-isci">{t('requests.employeeSuffix')}</span>
              <span className="lb-aktiv">{t('requests.activeSuffix')}</span>
            </div>
          )}
          <div className={'dept-cards-grid' + (deptGorunus === 'kafel' ? ' kafel' : ' lovhe')}>
            {gosterilenDepts.map((d, sira) => {
              const cnt = deptActiveCount(d);
              const newCnt = deptNewCount(d.id);
              const icon = DEPT_ICONS[d.code] || DEPT_ICONS.GENERAL;
              const theme = DEPT_THEME[d.code] || DEPT_THEME.GENERAL;
              const desc = DEPT_DESCS[d.code] || t('requests.deptGENERAL');
              const deptName = lang === 'en' ? t('departments.' + d.name, d.name) : d.name;
              const cardStyle = { '--dept-color': theme.color, '--dept-soft': theme.soft, '--dept-tint': theme.tint };
              return (
                <div key={d.id}
                     className={'dept-req-card' + (newCnt > 0 ? ' has-new' : '')
                       /* Hər kampusun eyni adlı şöbəsi var — admin qarışıq
                          siyahıda hansının hansı olduğunu ayıra bilsin */
                       + (d.branch_code && d.branch_code !== 'MAIN' ? ' other-branch' : '')}
                     style={cardStyle} onClick={() => selectDept(d)}>
                  <span className="dept-req-accent" aria-hidden="true" />
                  {/* Sıra nömrəsi — kataloq hissi verir, gözü aşağı aparır */}
                  <span className="dept-index" aria-hidden="true">
                    {String(sira + 1).padStart(2, '0')}
                  </span>
                  {/* Məktəbin gerbindəki dağ silueti — şöbənin rəngində,
                      yalnız kölgə kimi. Hər kartın öz imzası budur. */}
                  <svg className="dept-dag" viewBox="0 0 160 48" aria-hidden="true"
                       fill="none" stroke="currentColor" strokeWidth="2.4"
                       strokeLinecap="round" strokeLinejoin="round" preserveAspectRatio="none">
                    <path d="M2 46 L44 12 L66 30 L86 6 L108 26 L130 14 L158 46" />
                  </svg>
                  {newCnt > 0 && (
                    <span className="dept-new-badge" title={`${newCnt} ${t('requests.newSuffix') || 'yeni'}`}>
                      {newCnt}
                    </span>
                  )}
                  <div className="dept-req-icon">{icon}</div>
                  <div className="dept-req-info">
                    <div className="dept-req-name">{deptName}</div>
                    <div className="dept-req-desc">{desc}</div>
                    <div className="dept-req-pills">
                      {d.branch_code && d.branch_code !== 'MAIN' && (
                        <span className="dept-pill dept-pill-branch">
                          {d.branch_name || d.branch_code}
                        </span>
                      )}
                      <span className="dept-pill dept-pill-staff">
                        {d.member_count || 0} {t('requests.employeeSuffix')}
                      </span>
                    </div>
                  </div>
                  {/* Aktiv sorğu sayı — çip yerinə iri rəqəm */}
                  <div className={'dept-stat' + (cnt > 0 ? ' on' : '')}>
                    <b>{cnt > 0 ? cnt : '—'}</b>
                    <span>{t('requests.activeSuffix')}</span>
                  </div>
                  <span className="dept-req-arrow">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                  </span>
                </div>
              );
            })}
          </div>
          </div>
          )}
        </>
      )}

      {/* Selected department - ticket list */}
      {activeDept && (
        <>
          {/* Başlıq, filtrlər və siyahı vahid panelin bölmələridir */}
          <div className="req-panel">
          <div className="req-header">
            <div style={{display:'flex',alignItems:'center',gap:12}}>
              <button className="req-back-btn" onClick={backToDepts}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
              </button>
              <div>
                <h2>{lang === 'en' ? t('departments.' + activeDept.name, activeDept.name) : activeDept.name}</h2>
                <span className="req-count">{tickets.length} {t('requests.requestSuffix')}</span>
              </div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              {/* Hidden while a filter is on — the endpoint wipes the whole dept, not the filtered view */}
              {isAdmin && tickets.length > 0 && !fStatus && !fPriority && !fMine && (
                <button className="btn-wipe-all" onClick={deleteAllInDept} title="Bu şöbənin bütün sorğularını sil">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"/></svg>
                  Hamısını sil
                </button>
              )}
              <button className="btn-primary" onClick={() => navigate('/requests/new', { state: { deptId: activeDept.id, deptCode: activeDept.code } })}>{t('requests.btnNewShort')}</button>
            </div>
          </div>
          <div className="req-filters">
            <select value={fStatus} onChange={e => setFStatus(e.target.value)}>
              <option value="">{t('requests.allStatuses')}</option>
              {Object.entries(STATUS_LABELS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select value={fPriority} onChange={e => setFPriority(e.target.value)}>
              <option value="">{t('requests.priorityFilter')}</option>
              {Object.entries(PRIORITY_LABELS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <label className="filter-check"><input type="checkbox" checked={fMine} onChange={e => setFMine(e.target.checked)} /> {t('requests.mineFilter')}</label>
          </div>
          {loadError && <div style={{padding:'14px',background:'#fee2e2',color:'#dc2626',borderRadius:8,marginBottom:12,fontSize:'0.85rem'}}><Icon name="warning" size={15} /> {loadError}</div>}
          {loading ? <div className="req-loading">{t('common.loading')}</div> : (
            <div className="ticket-list">
              {tickets.length === 0 && <div className="req-empty">{t('requests.noRequestsShort')}</div>}
              {tickets.map(tk => {
                const st = STATUS_LABELS[tk.status] || STATUS_LABELS.open;
                const pr = PRIORITY_LABELS[tk.priority] || PRIORITY_LABELS.medium;
                return (
                  <div key={tk.id}
                       className={'ticket-card'
                         + (tk.sla_breached ? ' sla-breach' : '')
                         /* Baş Kampusdan kənar sorğular ayrıca rənglə seçilir —
                            qarışıq siyahıda hansı kampusa aid olduğu dərhal görünsün */
                         + (tk.branch_code && tk.branch_code !== 'MAIN' ? ' other-branch' : '')}
                       onClick={() => openDetail(tk)}>
                    <div className="ticket-top">
                      <span className="ticket-num">{tk.request_number}</span>
                      {tk.branch_code && tk.branch_code !== 'MAIN' && (
                        <span className="ticket-branch">{tk.branch_name || tk.branch_code}</span>
                      )}
                      <span className="ticket-dept">{lang === 'en' ? t('departments.' + tk.department_name, tk.department_name) : tk.department_name}</span>
                      {tk.sla_breached && <span className="sla-badge breached">{t('requests.slaBreached')}</span>}
                      <span style={{marginLeft:'auto',color:pr.color,fontWeight:600,fontSize:'0.78rem'}}>&#9650; {pr.label}</span>
                      <span className="ticket-status" style={{background:st.bg,color:st.color}}>{st.label}</span>
                      {isAdmin && (
                        <button className="ticket-del-btn" onClick={e => deleteTicket(tk, e)} title="Sorğunu sil">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6"/></svg>
                        </button>
                      )}
                    </div>
                    <div className="ticket-title">{tk.title}</div>
                    <div className="ticket-meta">
                      <span>{tk.requester_name}</span>
                      {tk.assignee_name && <span>&#8594; {tk.assignee_name}</span>}
                      <span>{fmtShort(tk.created_at)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          </div>{/* /req-panel */}
        </>
      )}


      {showNew && (
        <div className="modal-overlay" onClick={() => setShowNew(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-hdr">
              <h3>{isNewHR ? t('requests.modalLeaveTitle') : t('requests.modalNewTitle')}</h3>
              <button onClick={() => setShowNew(false)}><Icon name="close" size={16} /></button>
            </div>
            <form onSubmit={submitNew} className="modal-body">
              <label>{t('requests.fieldDept')}</label>
              <select value={newDept} onChange={e => setNewDept(e.target.value)} required>
                <option value="">{t('requests.selectDept')}</option>
                {departments.map(d => <option key={d.id} value={d.id}>{lang === 'en' ? t('departments.' + d.name, d.name) : d.name}</option>)}
              </select>

              {isNewHR ? (
                <>
                  <label>{t('requests.fieldLeaveType')}</label>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:7, marginTop:2 }}>
                    {[
                      { v:'medical',   l:t('requests.leaveMedical'),   icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7V17a2 2 0 002 2h14a2 2 0 002-2V7"/><path d="M1 7h22"/><path d="M8 3h8a2 2 0 012 2v2H6V5a2 2 0 012-2z"/><path d="M12 11v6M9 14h6"/></svg>, d:t('requests.leaveMedicalDesc') },
                      { v:'family',    l:t('requests.leaveFamily'),    icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="6" r="2.5"/><circle cx="16" cy="6" r="2.5"/><path d="M3 20v-2a4 4 0 014-4h2a4 4 0 014 4v2"/><path d="M15 14a4 4 0 014 4v2"/></svg>, d:t('requests.leaveFamilyDesc') },
                      { v:'personal',  l:t('requests.leavePersonal'),  icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="7" r="4"/><path d="M5.5 21v-2a6.5 6.5 0 0113 0v2"/></svg>, d:t('requests.leavePersonalDesc') },
                      { v:'education', l:t('requests.leaveEducation'), icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3L2 8l10 5 10-5-10-5z"/><path d="M2 8v6"/><path d="M6 10.5v5a6 3 0 0012 0v-5"/></svg>, d:t('requests.leaveEducationDesc') },
                      { v:'other',     l:t('requests.leaveOther'),     icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>, d:t('requests.leaveOtherDesc') },
                    ].map(it => (
                      <label key={it.v} style={{
                        border:'1.5px solid '+(newAbsType===it.v?'#007c82':'#e5e7eb'),
                        background:newAbsType===it.v?'#e0f2f4':'#fff',
                        borderRadius:8, padding:'8px 10px', cursor:'pointer',
                        display:'flex', flexDirection:'column', gap:2
                      }}>
                        <input type="radio" style={{display:'none'}} checked={newAbsType===it.v}
                          onChange={() => { setNewAbsType(it.v); setNewFamReason(''); setNewEduDoc(''); }} />
                        <span style={{color:newAbsType===it.v?'#007c82':'#6b7280'}}>{it.icon}</span>
                        <span style={{fontWeight:700,fontSize:'0.79rem',color:newAbsType===it.v?'#007c82':'#374151'}}>{it.l}</span>
                        <span style={{fontSize:'0.67rem',color:'#9ca3af'}}>{it.d}</span>
                      </label>
                    ))}
                  </div>

                  {newAbsType === 'family' && (
                    <>
                      <label>Ailə səbəbi *</label>
                      <select value={newFamReason} onChange={e => setNewFamReason(e.target.value)} required>
                        <option value="">Seçin</option>
                        <option value="death">Birinci dərəcəli qohumun vəfatı</option>
                        <option value="own_wedding">Özünün toyu</option>
                        <option value="child_wedding">Oğul / qızın toyu</option>
                      </select>
                    </>
                  )}

                  {newAbsType === 'education' && (
                    <>
                      <label>Müvafiq sənəd varmı?</label>
                      <select value={newEduDoc}
                              onChange={e => { setNewEduDoc(e.target.value); if (e.target.value !== 'yes') setNewEduFiles([]); }}>
                        <option value="">Seçin</option>
                        <option value="yes">Bəli, sənəd var</option>
                        <option value="no">Xeyr, izahat tələb olunur</option>
                      </select>
                    </>
                  )}
                  {newAbsType === 'education' && newEduDoc === 'yes' && (
                    <>
                      {/* Arayışın özü — rəhbər, HR və direktor ərizənin içində açır */}
                      <label>Sənədi yükləyin *</label>
                      <input type="file" multiple
                             accept=".pdf,.jpg,.jpeg,.png,.heic,.webp,.doc,.docx"
                             onChange={e => setNewEduFiles(Array.from(e.target.files || []).slice(0, 3))} />
                      <div style={{ fontSize:'0.72rem', color:'#9ca3af', marginTop:4 }}>
                        PDF, şəkil və ya Word. Ən çoxu 3 fayl, hər biri 15 MB-a qədər.
                      </div>
                    </>
                  )}

                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                    <div>
                      <label>Başlama tarixi *</label>
                      <DateTimeField value={newLeaveStart} onChange={setNewLeaveStart} required minDate={bugunISO()} />
                    </div>
                    <div>
                      <label>Bitmə tarixi *</label>
                      <DateTimeField value={newLeaveEnd} onChange={setNewLeaveEnd} required
                        minDate={newLeaveStart ? newLeaveStart.slice(0, 10) : bugunISO()} />
                    </div>
                  </div>

                  {['medical','personal','education','other'].includes(newAbsType) && (
                    <>
                      <label>{newAbsType === 'medical' ? 'Qeyd (istəyə bağlı)' : 'İzahat *'}</label>
                      <textarea value={newLeaveExpl} onChange={e => setNewLeaveExpl(e.target.value)}
                        required={newAbsType !== 'medical'} rows={3}
                        placeholder="İşburaxma səbəbini izah edin..." />
                    </>
                  )}
                </>
              ) : (
                <>
                  <label>Müraciət növü</label>
                  <select value={newType} onChange={e => setNewType(e.target.value)} disabled={!newDept}>
                    <option value="">Növ seçin</option>
                    {reqTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>

                  {isNewIT && (
                    <div className="it-fields">
                      <div className="it-fields-title">IT Məlumatları</div>
                      <label>Kateqoriya</label>
                      <select value={newCategory} onChange={e => setNewCategory(e.target.value)}>
                        <option value="">Seçin</option>
                        {IT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <label>Sinif / Otaq</label>
                      <OtaqSecici value={newRoom} onChange={setNewRoom} placeholder="Seçin" />
                      <label>Cihaz / Kompüter adı</label>
                      <input value={newDevice} onChange={e => setNewDevice(e.target.value)} placeholder="PC-123 / DELL-ELDAR" />
                      <label>Əməliyyat sistemi</label>
                      <select value={newOS} onChange={e => setNewOS(e.target.value)}>
                        <option value="">Seçin</option>
                        {IT_OS.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                      <label>Xəta mesajı (varsa)</label>
                      <textarea value={newErrorMsg} onChange={e => setNewErrorMsg(e.target.value)} rows={2} placeholder="Xətanın tam mətnini bura yazın..." />
                    </div>
                  )}

                  {isNewFinance && (
                    <div className="it-fields">
                      <div className="it-fields-title">Maliyyə Məlumatları</div>
                      {reqTypes.find(t => t.id === parseInt(newType) && t.name.includes('hissə-hissə')) && (
                        <>
                          <label>Ödəmə müddəti *</label>
                          <div style={{ display:'flex', gap:8 }}>
                            {[3,6,12].map(m => (
                              <label key={m} style={{
                                border:'1.5px solid '+(newInstallMonths===String(m)?'#007c82':'#e5e7eb'),
                                background:newInstallMonths===String(m)?'#e0f2f4':'#fff',
                                borderRadius:8, padding:'8px 16px', cursor:'pointer',
                                fontWeight:600, fontSize:'0.88rem',
                                color:newInstallMonths===String(m)?'#007c82':'#374151'
                              }}>
                                <input type="radio" style={{display:'none'}} checked={newInstallMonths===String(m)}
                                  onChange={() => setNewInstallMonths(String(m))} />
                                {m} ay
                              </label>
                            ))}
                          </div>
                        </>
                      )}
                      {reqTypes.find(t => t.id === parseInt(newType) && t.name.includes('pay slip')) && (
                        <>
                          <label>Hansı ay üçün? *</label>
                          <input type="month" value={newPaySlipMonth} onChange={e => setNewPaySlipMonth(e.target.value)}
                            style={{width:'100%',padding:'8px 11px',border:'1px solid #d1d5db',borderRadius:8,fontSize:'0.88rem',boxSizing:'border-box'}} />
                        </>
                      )}
                    </div>
                  )}

                  <label>{isNewFinance ? 'Müraciətin mövzusu *' : 'Başlıq *'}</label>
                  <input value={newTitle} onChange={e => setNewTitle(e.target.value)} required placeholder={isNewFinance ? 'Müraciətin qısa təsviri' : 'Problemin qısa təsviri'} />

                  <label>{isNewFinance ? 'Əlavə qeyd' : 'Ətraflı təsvir'}</label>
                  <textarea value={newDesc} onChange={e => setNewDesc(e.target.value)} rows={4} placeholder={isNewFinance ? 'Əlavə məlumat və ya izahat...' : 'Problem haqqında ətraflı yazın...'} />

                  {!isNewFinance && (
                    <>
                      <label>Prioritet</label>
                      <div className="priority-grid">
                        {Object.entries(PRIORITY).map(([k,v]) => (
                          <label key={k} className={'priority-opt' + (newPriority === k ? ' sel' : '')}>
                            <input type="radio" name="priority" value={k} checked={newPriority === k} onChange={() => setNewPriority(k)} />
                            <span style={{ color: v.color }}>&#9650; {v.label}</span>
                            <small>{v.sla}</small>
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowNew(false)}>Ləğv et</button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Göndərilir...' : (isNewHR ? 'Ərizəni Göndər' : 'Göndər')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selected && detail && (
        <div className="modal-overlay" onClick={closeDetail}>
          <div className="modal modal-xl" onClick={e => e.stopPropagation()}>
            <div className="detail-hdr">
              <div className="detail-hdr-left">
                <span className="ticket-num">{detail.request_number}</span>
                {detail.branch_code && detail.branch_code !== 'MAIN' && (
                  <span className="ticket-branch">{detail.branch_name || detail.branch_code}</span>
                )}
                <h3>{detail.title}</h3>
              </div>
              <div className="detail-hdr-right">
                <span className="ticket-status lg" style={{ background: (STATUS_LABELS[detail.status]||STATUS_LABELS.open).bg, color: (STATUS_LABELS[detail.status]||STATUS_LABELS.open).color }}>
                  {(STATUS_LABELS[detail.status]||STATUS_LABELS.open).label}
                </span>
                <button className="btn-close-x" onClick={closeDetail}>X</button>
              </div>
            </div>

            <div className="detail-actions">
              {(user.role === 'admin' || isAssignee(detail)) && detail.status === 'assigned' && (
                <button className="act-btn start" onClick={doStart}>&#9654; İşə başla (timer)</button>
              )}
              {(user.role === 'admin' || isAssignee(detail)) && detail.status === 'in_progress' && (
                <button className="act-btn resolve" onClick={() => setShowResolve(true)}>&#10003; Həll edildi (timer dayandır)</button>
              )}
              {/* Tapşırıq artıq kiməsə verilibsə, bunu açıq yazırıq. Əvvəl eyni
                  "Təyin et" düyməsi qalırdı və təyinat baş tutmamış kimi görünürdü. */}
              {detail.status === 'assigned' && detail.assignee_name && !isAssignee(detail) && (
                <div className="detail-assigned-note">
                  <b>{detail.assignee_name}</b> üzərindədir — işə başlamasını gözləyir
                </div>
              )}
              {(user.role === 'admin' || user.role === 'department_head') && ['open','assigned'].includes(detail.status) && (
                <button className="act-btn assign" onClick={() => setShowAssign(true)}>
                  {detail.status === 'assigned' ? 'Başqasına təyin et' : 'Təyin et'}
                </button>
              )}
              {isRequester(detail) && detail.status === 'resolved' && (
                <button className="act-btn rate" onClick={() => setShowRate(true)}>&#9733; Qiymətləndir və bağla</button>
              )}
              {detail.status === 'in_progress' && detail.accepted_at && (
                <div className="detail-timer">
                  Timer: <LiveTimer acceptedAt={detail.accepted_at} resolvedAt={detail.resolved_at} />
                </div>
              )}
              {(detail.status === 'resolved' || detail.status === 'closed') && detail.time_spent_minutes != null && (
                <div className="detail-timer done">{t('requests.detailTimeSpent')} {fmtTime(detail.time_spent_minutes)}</div>
              )}
              {detail.rating && (
                <div className="detail-rating-display">
                  <Stars value={detail.rating} />
                  {detail.rating_comment && <span className="rating-note">"{detail.rating_comment}"</span>}
                </div>
              )}
            </div>

            <div className="detail-tabs">
              <button className={'tab-btn' + (detailTab === 'info' ? ' active' : '')} onClick={() => setDetailTab('info')}>{t('requests.detailTabInfo')}</button>
              <button className={'tab-btn' + (detailTab === 'comments' ? ' active' : '')} onClick={() => setDetailTab('comments')}>{t('requests.detailTabComments')} ({comments.length})</button>
              <button className={'tab-btn' + (detailTab === 'audit' ? ' active' : '')} onClick={() => setDetailTab('audit')}>{t('requests.detailTabHistory')} ({audit.length})</button>
            </div>

            <div className="detail-body">
              {detailTab === 'info' && (() => {
                const cf = getCF(detail);
                const itTicket = isIT(detail);
                return (
                  <div className="info-grid">
                    <div className="info-left">
                      <div className="info-section">
                        <label>{t('requests.detailLabelRequester')}</label><span>{detail.requester_name}</span>
                        <label>{t('requests.detailLabelAssignee')}</label><span>{detail.assignee_name || '—'}</span>
                        <label>{t('requests.detailLabelDept')}</label><span>{lang === 'en' ? t('departments.' + detail.department_name, detail.department_name) : detail.department_name}</span>
                        <label>{t('requests.detailLabelType')}</label><span>{detail.type_name || '—'}</span>
                        <label>{t('requests.detailLabelCreated')}</label><span>{fmt(detail.created_at)}</span>
                        <label>{t('requests.detailLabelSlaEnd')}</label><span className={detail.sla_breached ? 'text-red' : ''}>{fmt(detail.sla_deadline)}</span>
                      </div>
                    </div>
                    <div className="info-right">
                      {/* Forma 1 — mülki-hüquq müqaviləsi sorğusu */}
                      {cf.form === 'legal_contract_f1' && (
                        <div className="info-section legal-section">
                          <div className="section-title">{t('legal.detailTitle')}</div>

                          <label>{t('legal.fieldRequester')}</label><span>{cf.requester_full || '—'}</span>
                          {cf.requester_position && <><label>{t('legal.fieldPosition')}</label><span>{cf.requester_position}</span></>}

                          <label>{t('legal.fieldParty')}</label><span><b>{cf.party_name}</b></span>
                          {cf.party_contact && <><label>{t('legal.fieldContact')}</label><span>{cf.party_contact}</span></>}
                          {cf.party_phone && <><label>{t('legal.fieldPhone')}</label><span className="mono">{cf.party_phone}</span></>}
                          {cf.party_email && <><label>{t('legal.fieldEmail')}</label><span className="mono">{cf.party_email}</span></>}

                          <label>{t('legal.fieldSubject')}</label><span>{cf.contract_subject}</span>
                          <label>{t('legal.fieldPrice')}</label><span>{cf.contract_price}</span>
                          {cf.contract_start && <><label>{t('legal.fieldStart')}</label><span>{cf.contract_start}</span></>}
                          {cf.contract_duration && <><label>{t('legal.fieldDuration')}</label><span>{cf.contract_duration}</span></>}
                          {cf.contract_special && <><label>{t('legal.fieldSpecial')}</label><span>{cf.contract_special}</span></>}

                          {/* Təsdiq zənciri */}
                          {legalFlow && (
                            <div className="legal-flow">
                              <div className="section-title">{t('legal.flowTitle')}</div>

                              {legalFlow.stage === 'rejected' ? (
                                <div className="legal-rejected">
                                  <Icon name="closeCircle" size={15} />
                                  <div>
                                    <b>{t('legal.rejectedAt')}: {
                                      (LEGAL_STEPS.find(s => s.k === legalFlow.rejected_stage) || {}).l || legalFlow.rejected_stage
                                    }</b>
                                    {legalFlow.rejected_note && <div>{legalFlow.rejected_note}</div>}
                                  </div>
                                </div>
                              ) : (
                                <ol className="legal-steps">
                                  {LEGAL_STEPS.map(s => {
                                    const order = LEGAL_STEPS.findIndex(x => x.k === s.k);
                                    const curIdx = legalFlow.stage === 'completed'
                                      ? LEGAL_STEPS.length
                                      : LEGAL_STEPS.findIndex(x => x.k === legalFlow.stage);
                                    const done = order < curIdx;
                                    const active = order === curIdx;
                                    const at = { lawyer_review: legalFlow.lawyer_at, finance: legalFlow.finance_at,
                                                 director: legalFlow.director_at, execution: legalFlow.executed_at }[s.k];
                                    return (
                                      <li key={s.k} className={done ? 'done' : active ? 'active' : ''}>
                                        <span className="ls-dot">{done ? <Icon name="check" size={11} /> : order + 1}</span>
                                        <span className="ls-body">
                                          <b>{s.l}</b>
                                          <small>{s.who}{at ? ' · ' + fmtShort(at) : ''}</small>
                                        </span>
                                      </li>
                                    );
                                  })}
                                </ol>
                              )}

                              {legalFlow.my_turn && (
                                <div className="legal-actions">
                                  <button className="lg-approve" disabled={legalBusy} onClick={() => legalAct('approve')}>
                                    <Icon name="check" size={14} />
                                    {legalFlow.stage === 'execution' ? t('legal.btnExecute')
                                      : legalFlow.stage === 'lawyer_review' ? t('legal.btnReviewed')
                                      : t('legal.btnApprove')}
                                  </button>
                                  <button className="lg-reject" disabled={legalBusy} onClick={() => legalAct('reject')}>
                                    <Icon name="close" size={14} /> {t('legal.btnReject')}
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          <div className="legal-signs">
                            <div className="section-title">{t('legal.sec4')}</div>
                            <div><span>{t('legal.signDirector')}</span><b>Tural Abbasov</b></div>
                            <div><span>{t('legal.signFinance')}</span><b>Mehman Babayev</b></div>
                            <div><span>{t('legal.signRequester')}</span><b>{cf.requester_full || detail.requester_name}</b></div>
                          </div>

                          <button type="button" className="legal-print-btn" onClick={() => window.print()}>
                            <Icon name="printer" size={14} /> {t('legal.btnPrint')}
                          </button>
                        </div>
                      )}

                      {itTicket && (cf.category || cf.room || cf.device || cf.os || cf.error_msg) && (
                        <div className="info-section it-section">
                          <div className="section-title">{t('requests.itInfoTitle')}</div>
                          {cf.category && <><label>{t('requests.itCategory')}</label><span>{cf.category}</span></>}
                          {cf.room     && <><label>{t('requests.itRoom')}</label><span>{cf.room}</span></>}
                          {cf.device   && <><label>{t('requests.itDevice')}</label><span className="mono">{cf.device}</span></>}
                          {cf.os       && <><label>{t('requests.itOS')}</label><span>{cf.os}</span></>}
                          {cf.error_msg && <><label>{t('requests.itError')}</label><span className="error-text">{cf.error_msg}</span></>}
                        </div>
                      )}
                      {(cf.installment_months || cf.pay_slip_month || cf.amount || cf.requested_date) && (
                        <div className="info-section it-section">
                          <div className="section-title">{t('finance.info')}</div>
                          {cf.amount && <><label>{t('finance.amount')}</label><span>{cf.amount} AZN</span></>}
                          {cf.requested_date && <><label>{t('finance.requestedDate')}</label><span>{cf.requested_date}</span></>}
                          {cf.installment_months && <><label>{t('finance.paymentTerm')}</label><span>{cf.installment_months}</span></>}
                          {cf.pay_slip_month && <><label>Pay slip</label><span>{cf.pay_slip_month}</span></>}
                        </div>
                      )}
                      {(cf.location || cf.room || cf.access_time || cf.photo_url) && (
                        <div className="info-section it-section" style={{borderColor:'rgba(234,88,12,0.2)',background:'linear-gradient(135deg,#fff7ed 0%,#ffedd5 100%)'}}>
                          <div className="section-title">{t('maintenance.title')}</div>
                          {cf.location && (<><label>{t('maintenance.detailLocation')}</label><span style={{fontWeight:700,color:'#9a3412'}}>{cf.location}</span></>)}
                          {cf.room && (<><label>{t('maintenance.detailRoom')}</label><span>{cf.room}</span></>)}
                          {cf.access_time && (<><label>{t('maintenance.detailAccess')}</label><span>{t('maintenance.access' + cf.access_time.charAt(0).toUpperCase() + cf.access_time.slice(1), cf.access_time)}</span></>)}
                          {cf.photo_url && (
                            <><label>{t('maintenance.detailPhoto')}</label>
                              <span style={{display:'block'}}>
                                <a href={cf.photo_url + (cf.photo_url.includes('?') ? '&' : '?') + 'token=' + (localStorage.getItem('token') || '')} target="_blank" rel="noopener noreferrer" download={cf.photo_name || ''}
                                  style={{
                                    display:'inline-flex',alignItems:'center',gap:8,
                                    padding:'8px 14px',borderRadius:10,
                                    background:'#fff',
                                    border:'1px solid rgba(234,88,12,0.3)',
                                    color:'#9a3412',fontSize:'0.86rem',fontWeight:700,
                                    textDecoration:'none'
                                  }}>
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                                    <polyline points="14,2 14,8 20,8"/>
                                  </svg>
                                  {cf.photo_name || t('maintenance.detailPhoto')}
                                </a>
                              </span>
                            </>
                          )}
                        </div>
                      )}
                      {(cf.publication_date || cf.material_url || (cf.materials || []).length > 0) && (
                        <div className="info-section it-section pub-info-section">
                          <div className="section-title">{t('publication.title')}</div>
                          {cf.publication_date && (
                            <><label>{t('publication.fieldDate')}</label>
                              <span style={{fontWeight:700,color:'#7c3aed'}}>{new Date(cf.publication_date).toLocaleDateString(lang === 'en' ? 'en-US' : 'az-AZ')}</span>
                            </>
                          )}
                          {/* Köhnə sorğularda bir fayl (material_url), yenilərdə
                              bir neçə fayl (materials) olur — ikisi də göstərilir. */}
                          {(() => {
                            const mats = (cf.materials || []).length
                              ? cf.materials
                              : (cf.material_url ? [{ url: cf.material_url, name: cf.material_name }] : []);
                            if (!mats.length) return null;
                            const tkn = 'token=' + (localStorage.getItem('token') || '');
                            return (
                              <>
                                <label>{t('publication.fieldMaterial')}</label>
                                <span style={{display:'block'}}>
                                  {mats.map((m, i) => {
                                    const href = m.url + (m.url.includes('?') ? '&' : '?') + tkn;
                                    return (
                                      <span key={m.url + i} style={{display:'flex',flexWrap:'wrap',alignItems:'center',gap:8,marginBottom: i < mats.length - 1 ? 8 : 0}}>
                                        <a href={href} target="_blank" rel="noopener noreferrer" download={m.name || ''}
                                          style={{
                                            display:'inline-flex',alignItems:'center',gap:8,minWidth:0,
                                            padding:'8px 14px',borderRadius:10,
                                            background:'linear-gradient(135deg,#ede9fe 0%,#f5f3ff 100%)',
                                            border:'1px solid rgba(124,58,237,0.22)',
                                            color:'#6d28d9',fontSize:'0.86rem',fontWeight:700,
                                            textDecoration:'none',transition:'all .18s'
                                          }}
                                          onMouseEnter={e => { e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.boxShadow='0 4px 14px rgba(124,58,237,0.22)'; }}
                                          onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}>
                                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{flex:'0 0 16px'}}>
                                            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                                            <polyline points="14,2 14,8 20,8"/>
                                          </svg>
                                          <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                                            {m.name || t('publication.fieldMaterial')}
                                          </span>
                                        </a>
                                        <a href={href} download={m.name || ''}
                                          style={{
                                            display:'inline-flex',alignItems:'center',gap:6,
                                            padding:'8px 14px',borderRadius:10,
                                            background:'#1e40af',color:'#fff',
                                            fontSize:'0.82rem',fontWeight:700,textDecoration:'none',
                                          }}>
                                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7,10 12,15 17,10"/><line x1="12" y1="15" x2="12" y2="3"/>
                                          </svg>
                                          {t('publication.btnDownload')}
                                        </a>
                                      </span>
                                    );
                                  })}
                                </span>
                              </>
                            );
                          })()}
                        </div>
                      )}
                      <div className="desc-box">
                        <label>{t('requests.detailDescription')}</label>
                        <p>{detail.description || '—'}</p>
                      </div>
                      {detail.resolution && (
                        <div className="resolution-box">
                          <label>{t('requests.detailResolution')}</label>
                          <p>{detail.resolution}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {detailTab === 'comments' && (
                <div className="comments-wrap">
                  <div className="comments-list">
                    {comments.length === 0 && <div className="empty-note">{t('requests.noComments')}</div>}
                    {comments.map(c => (
                      <div key={c.id} className={'comment-item' + (c.is_internal ? ' internal' : '')}>
                        <div className="comment-meta">
                          <strong>{c.author_name}</strong>
                          {c.is_internal && <span className="internal-tag">Daxili</span>}
                          <span className="comment-time">{fmt(c.created_at)}</span>
                        </div>
                        <p>{c.comment}</p>
                        {/* Həmin şərhə qoşulan fayllar */}
                        {attachments.filter(a => a.comment_id === c.id).length > 0 && (
                          <div className="comment-files">
                            {attachments.filter(a => a.comment_id === c.id).map(a => (
                              <button key={a.id} type="button" className="att-chip"
                                onClick={() => openAttachment(a)} title={a.original_name}>
                                <Icon name="paperclip" size={13} />
                                <span className="att-name">{a.original_name}</span>
                                <span className="att-size">{fmtSize(a.size_bytes)}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <form className="comment-form" onSubmit={addComment}>
                    <textarea value={newComment} onChange={e => setNewComment(e.target.value)} rows={2} placeholder={t('requests.writeComment')} />
                    {/* Göndərilməmiş fayllar — göndərməzdən əvvəl çıxarıla bilər */}
                    {pendingFiles.length > 0 && (
                      <div className="comment-files pending">
                        {pendingFiles.map((f, i) => (
                          <span key={i} className="att-chip pending">
                            <Icon name="paperclip" size={13} />
                            <span className="att-name">{f.name}</span>
                            <span className="att-size">{fmtSize(f.size)}</span>
                            <button type="button" className="att-x"
                              onClick={() => setPendingFiles(p => p.filter((_, ix) => ix !== i))}>×</button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="comment-form-footer">
                      <label className="att-pick" title={t('requests.attachHint')}>
                        <Icon name="paperclip" size={15} />
                        <input type="file" multiple style={{display:'none'}}
                          onChange={e => {
                            const picked = Array.from(e.target.files || []);
                            // Server bir dəfəyə 5 fayl və 15 MB qəbul edir
                            const ok = picked.filter(f => f.size <= 15 * 1024 * 1024);
                            if (ok.length < picked.length) alert(t('requests.attachTooBig'));
                            setPendingFiles(p => [...p, ...ok].slice(0, 5));
                            e.target.value = '';
                          }} />
                      </label>
                      <label className="internal-chk">
                        <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} />
                        {t('requests.detailLabelType') === 'NÖV' ? 'Daxili şərh' : 'Internal note'}
                      </label>
                      <button type="submit" className="btn-primary sm" disabled={uploadingAtt}>
                        {uploadingAtt ? t('requests.attachSending') : t('common.send')}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {detailTab === 'audit' && (
                <div className="audit-list">
                  {audit.length === 0 && <div className="empty-note">Tarixçə yoxdur</div>}
                  {audit.map(a => (
                    <div key={a.id} className="audit-item">
                      <div className="audit-dot" />
                      <div className="audit-content">
                        <span className="audit-user">{a.user_name}</span>
                        <span className="audit-action">{a.action}</span>
                        {a.old_value && a.new_value && (
                          <span className="audit-change">{a.old_value} &#8594; {a.new_value}</span>
                        )}
                        {a.note && <span className="audit-note">{a.note}</span>}
                        <span className="audit-time">{fmt(a.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {showAssign && (
              <div className="sub-panel">
                <div className="sub-panel-hdr">
                  <h4>Təyin et</h4>
                  <button onClick={() => setShowAssign(false)}><Icon name="close" size={16} /></button>
                </div>
                <label>İşçi seçin</label>
                <select value={assignTo} onChange={e => setAssignTo(e.target.value)}>
                  <option value="">Seçin</option>
                  {deptMembers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <label>Qeyd</label>
                <input value={assignNote} onChange={e => setAssignNote(e.target.value)} placeholder="İstəyə bağlı..." />
                <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                  <button className="btn-cancel" onClick={() => setShowAssign(false)}>Ləğv et</button>
                  <button className="btn-primary" onClick={doAssign}>Təyin et</button>
                </div>
              </div>
            )}

            {showResolve && (
              <div className="sub-panel">
                <div className="sub-panel-hdr">
                  <h4>Həll qeydi</h4>
                  <button onClick={() => setShowResolve(false)}><Icon name="close" size={16} /></button>
                </div>
                <label>Həll təsviri</label>
                <textarea value={resolution} onChange={e => setResolution(e.target.value)} rows={3} placeholder="Nə edildi?" />
                <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                  <button className="btn-cancel" onClick={() => setShowResolve(false)}>Ləğv et</button>
                  <button className="btn-primary" onClick={doResolve}>Həll edildi (timer dayandır)</button>
                </div>
              </div>
            )}

            {showRate && (
              <div className="sub-panel">
                <div className="sub-panel-hdr">
                  <h4>Xidməti Qiymətləndir</h4>
                  <button onClick={() => setShowRate(false)}><Icon name="close" size={16} /></button>
                </div>
                <div className="rate-section">
                  <p>Xidmət keyfiyyətini qiymətləndirin</p>
                  <div className="stars">
                    {[1,2,3,4,5].map(n => (
                      <button key={n} type="button" className={'star' + (n <= rateVal ? ' filled' : '')}
                        onClick={() => setRateVal(n)}>&#9733;</button>
                    ))}
                  </div>
                  <div className="rate-labels"><span>Pis</span><span>Əla</span></div>
                </div>
                <label>Şərh (istəyə bağlı)</label>
                <textarea value={rateComment} onChange={e => setRateComment(e.target.value)} rows={2} placeholder="Əlavə şərhiniz..." />
                <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                  <button className="btn-cancel" onClick={() => setShowRate(false)}>Ləğv et</button>
                  <button className="btn-primary" onClick={doRate}>Qiymətləndir və Bağla</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
